import * as React from "react"
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { Check, ChevronDown } from "lucide-react"

import { Button } from "components/ui/button"
import { useLanguage } from "contexts/LanguageContext"
import type { Language } from "i18n"

const flagClass = "h-3.5 w-5 shrink-0 rounded-[2px] ring-1 ring-border"

const FlagGB = () => (
  <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" className={flagClass} aria-hidden="true">
    <rect width="60" height="30" fill="#012169" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
    <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
)

const FlagFR = () => (
  <svg viewBox="0 0 3 2" preserveAspectRatio="xMidYMid slice" className={flagClass} aria-hidden="true">
    <rect width="1" height="2" fill="#002654" />
    <rect x="1" width="1" height="2" fill="#fff" />
    <rect x="2" width="1" height="2" fill="#CE1126" />
  </svg>
)

// Names stay in their own language so visitors can always find theirs.
const LANGUAGES: { code: Language; name: string; Flag: React.FC }[] = [
  { code: "en", name: "English", Flag: FlagGB },
  { code: "fr", name: "Français", Flag: FlagFR },
]

export function LanguageSelect() {
  const { language, setLanguage, t } = useLanguage()
  const current = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0]

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="sm" aria-label={t.languageSelect} className="gap-1.5 px-2">
          <current.Flag />
          <span className="text-xs font-semibold uppercase">{current.code}</span>
          <ChevronDown className="opacity-60" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[9rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <DropdownMenu.RadioGroup
            value={language}
            onValueChange={(value) => setLanguage(value as Language)}
          >
            {LANGUAGES.map(({ code, name, Flag }) => (
              <DropdownMenu.RadioItem
                key={code}
                value={code}
                className="flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted"
              >
                <Flag />
                <span className="flex-1">{name}</span>
                <DropdownMenu.ItemIndicator>
                  <Check className="h-4 w-4 text-primary" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
